import { observer } from 'mobx-react'
import { runInAction } from 'mobx'
import { Skeleton, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useEffect, useRef, useState, useCallback } from 'react'
import { uiController } from '@/controllers/uiController'
import BaseModal from '@/components/BaseModal/BaseModal'
import { commonUtils } from '@/helpers/commonUtils'
import { api } from '@/helpers/api'
import styles from './GoodsDetailModal.module.less'
import { IconImage } from '../Icon/Icon'
import VideoBox from '../VideoBox/VideoBox'
import { UserActionModuleEnum, UserActionElementEnum, UserActionEnum } from '@/helpers/commonConsts'
import { userService } from '@/services/userService'
import { GoodsDetailState } from './GoodsDetailState'
import classNames from 'classnames'

interface GoodsDetailProps {
  state: GoodsDetailState
}

// 热门素材推荐 item
const RecommendItem = observer(({ detail }: { detail: ServerData.PptData }) => {
  return (
    <div
      className={styles.hotRecommendItem}
      onClick={() => {
        runInAction(() => {
          uiController.goodsDetailModalProps.materialId = detail.id
        })
        userService.postUserActionRecord({
          module: UserActionModuleEnum.PC,
          function: '详情弹窗',
          position: '热门素材点击',
          element: UserActionElementEnum.BUTTON,
          action: UserActionEnum.CLICK,
        })
      }}
    >
      <div className={styles.hotRecommendItemImg}>
        <VideoBox
          key={detail.id}
          style={{
            position: 'absolute',
          }}
          loop={true}
          trigger='hover'
          src={detail.videoPath}
          poster={commonUtils.getimageView(detail.abbreviatePath, 230, 130)}
        />
      </div>
      <div className={styles.hotRecommendItemTitle}>{detail.name}</div>
    </div>
  )
})

// 热门素材推荐
const RecommendList = observer(({ state }: GoodsDetailProps) => {
  const recommendListRef = useRef<HTMLDivElement>(null)
  const [list, setList] = useState<ServerData.PptData[]>([])
  const [hasScroll, setHasScroll] = useState(false) // 是否需要滚动
  const [autoScroll, setAutoScroll] = useState(false) // 是否自动滚动
  const autoScrollTimerRef = useRef<NodeJS.Timeout | null>(null) // 自动滚动定时器
  const scrollAmountRef = useRef(0.5) // 每次移动的像素数量
  const scrollIntervalRef = useRef(16) // 使用约60fps的刷新率
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null) // 暂停自动滚动定时器
  const itemWidthRef = useRef(230) // 存储每个item的宽度
  const itemGapRef = useRef(18) // 存储每个item的间距
  const itemWRef = useRef(itemWidthRef.current + itemGapRef.current) // 存储每个item的宽度

  const containerWidthRef = useRef(0) // 容器宽度
  const [displayList, setDisplayList] = useState<ServerData.PptData[]>([]) // 用于显示的列表
  const [translateX, setTranslateX] = useState(0) // transform的X偏移量
  const [isTransitioning, setIsTransitioning] = useState(false) // 是否正在过渡中

  // 暂停自动滚动一段时间后恢复
  const pauseAutoScroll = useCallback((duration = 2000) => {
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current)
    }

    setAutoScroll(false)

    pauseTimeoutRef.current = setTimeout(() => {
      setAutoScroll(true)
      pauseTimeoutRef.current = null
    }, duration)
  }, [])

  // 初始化数据和滚动位置
  useEffect(() => {
    api.ppt.getPptRecommend(state.materialId).then((res) => {
      setList(res.dataList)
      if (recommendListRef.current) {
        containerWidthRef.current = recommendListRef.current.getBoundingClientRect().width
      }

      const count = Math.ceil((containerWidthRef.current - itemWidthRef.current) / itemWRef.current)

      // 初始化显示列表，添加额外的元素用于平滑过渡
      if (res.dataList.length > count) {
        setDisplayList([...res.dataList, ...res.dataList.slice(0, count + 1)])
        setAutoScroll(true)
        setHasScroll(true)
      } else {
        setDisplayList(res.dataList)
        setHasScroll(false)
      }
    })

    setTranslateX(0)

    return () => {
      if (autoScrollTimerRef.current) {
        clearInterval(autoScrollTimerRef.current)
      }
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current)
      }
    }
  }, [state.materialId])

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (autoScrollTimerRef.current) {
        clearInterval(autoScrollTimerRef.current)
      }
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current)
      }
    }
  }, [])

  // 自动滚动逻辑
  useEffect(() => {
    if (!autoScroll || !recommendListRef.current || displayList.length === 0 || isTransitioning)
      return

    const scrollElement = recommendListRef.current

    // 设置滚动间隔
    autoScrollTimerRef.current = setInterval(() => {
      if (!scrollElement) return

      // 计算新的偏移量
      const newTranslateX = translateX + scrollAmountRef.current
      setTranslateX(newTranslateX)

      // 计算需要重置的位置（当滚动到第一个循环结束时）
      const resetPoint = itemWRef.current * list.length

      // 检测是否需要重置位置
      if (newTranslateX >= resetPoint) {
        // 标记正在过渡
        setIsTransitioning(true)

        // 重置位置到开始
        setTranslateX(0)

        // 过渡结束后重置状态
        setTimeout(() => {
          setIsTransitioning(false)
        }, 50)
      }
    }, scrollIntervalRef.current)

    return () => {
      if (autoScrollTimerRef.current) {
        clearInterval(autoScrollTimerRef.current)
      }
    }
  }, [autoScroll, displayList.length, translateX, isTransitioning, list.length])

  // 手动滚动
  const onScroll = useCallback(
    (number: number) => {
      if (recommendListRef.current && !isTransitioning) {
        const nowX = translateX + itemWRef.current * number
        const maxX = itemWRef.current * list.length

        // 重置偏移点，以至此循环滚动
        const curX = nowX % maxX
        curX < 0 ? setTranslateX(maxX + curX) : setTranslateX(curX)

        pauseAutoScroll()
      }
    },
    [pauseAutoScroll, isTransitioning, translateX, list.length]
  )

  // 鼠标悬停时暂停滚动
  const handleMouseEnter = useCallback(() => setAutoScroll(false), [])

  // 鼠标离开时恢复滚动
  const handleMouseLeave = useCallback(() => setAutoScroll(true), [])

  if (!list.length) {
    return (
      <div className={styles.recommendList}>
        <div className={styles.hotRecommendList} ref={recommendListRef}>
          <div
            className={styles.hotRecommendListContent}
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: itemGapRef.current,
            }}
          >
            <Skeleton.Node active={true} style={{ width: 230, height: 165 }} />
            <Skeleton.Node active={true} style={{ width: 230, height: 165 }} />
            <Skeleton.Node active={true} style={{ width: 230, height: 165 }} />
            <Skeleton.Node active={true} style={{ width: 230, height: 165 }} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={styles.recommendWarp}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleMouseEnter}
      onTouchEnd={handleMouseLeave}
    >
      {hasScroll && (
        <div className={styles.nextScroll}>
          <div className={styles.icon} onClick={() => onScroll(-1)} />
        </div>
      )}
      <div className={styles.hotRecommendList} ref={recommendListRef}>
        <div
          className={styles.hotRecommendListContent}
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: itemGapRef.current,
            transform: `translateX(${-translateX}px)`,
            transition: isTransitioning ? 'none' : 'transform 0.016s linear',
          }}
        >
          {displayList.map((item, index) => (
            <RecommendItem key={`${item.id}-${index}`} detail={item} />
          ))}
        </div>
      </div>
      {hasScroll && (
        <div className={styles.prevScroll}>
          <div className={styles.icon} onClick={() => onScroll(1)} />
        </div>
      )}
    </div>
  )
})

// 按钮组
const GroupBtn = observer(({ state }: GoodsDetailProps) => {
  const [isConfirmExchange, setIsConfirmExchange] = useState(false)

  const onDownload = () => {
    if (state.downing) {
      return
    }

    userService.postUserActionRecord({
      module: UserActionModuleEnum.PC,
      function: '详情弹窗',
      position: '下载按钮',
      element: UserActionElementEnum.BUTTON,
      action: UserActionEnum.CLICK,
    })

    if (state.isDirectDownload) {
      if (uiController.tipWechat()) {
        return
      }
      uiController.modal.confirm({
        title: '提示',
        content: '确定下载该模版吗？',
        okText: '下载',
        cancelText: '取消',
        onOk: () => {
          state.download()
        },
      })
      return
    }

    if ((state.detail?.downloadInfo?.exchangeCodeRemeDownloadCount ?? 0) > 0) {
      setIsConfirmExchange(true)
      return
    }

    uiController.message.error('下载次数不足，请开通会员或购买')
  }

  const onConfirmExchange = async () => {
    setIsConfirmExchange(false)
    await state.download()
    userService.getUserInfo()
  }

  if (state.loading) {
    return (
      <div className={styles.buyBtns}>
        <Skeleton.Button active style={{ width: 128, height: 46 }} />
        <Skeleton.Button active style={{ height: 46 }} block />
      </div>
    )
  }

  return (
    <>
      <div className={styles.buyBtns}>
        {state.showDownloadBtn && (
          <div
            key={'downloadBtn'}
            className={classNames(userService.isVip ? styles.downloadBtn : styles.oneBuyBtn)}
            onClick={onDownload}
          >
            {state.downing && (
              <Spin indicator={<LoadingOutlined spin style={{ color: '#fff' }} />} />
            )}
            {!state.downing && userService.isVip && <IconImage src='Detail_IconDown' width={14} />}
            立即下载
            {userService.exchangeCodeCount > 0 && (
              <div className={styles.discountTips}>免费下载 {userService.exchangeCodeCount} 次</div>
            )}
            {!state.showOpenVipBtn && !state.isSingleBuy && (
              <div className={styles.downCountTips}>（ 剩余{state.buyDonwCount}次 ）</div>
            )}
          </div>
        )}

        {!state.showDownloadBtn && (
          <div
            key={'oneBuyBtn'}
            className={styles.oneBuyBtn}
            onClick={() => uiController.openVipModal(state.detail!)}
          >
            单个购买
          </div>
        )}
      {state.showOpenVipBtn && (
          // {!userService.isVip && (
            <div
              key={'openVipBtn'}
              className={styles.openVipBtn}
              onClick={() => uiController.openVipModal()}
            >
              <IconImage src='Detail_IconVip' width={22} height={15} />
              开通会员
              <div className={styles.discountTips}>低至¥7.45/个</div>
            </div>
          )}
      </div>
      <BaseModal
        open={isConfirmExchange}
        onClose={() => setIsConfirmExchange(false)}
        onOk={onConfirmExchange}
        title='提示'
        width={466}
        getContainer={false}
      >
        <div
          style={{
            fontFamily: 'PingFangSC, PingFang SC',
            fontWeight: 400,
            fontSize: 16,
            color: '#333333',
            lineHeight: '26px',
          }}
        >
          请问是否兑换该模版，兑换后将无法兑换其他模版哦～
        </div>
      </BaseModal>
    </>
  )
})

//
const GoodsInfo = observer(({ state }: GoodsDetailProps) => {
  if (state.loading && !state.detail) {
    return (
      <>
        <div className={styles.goodsTitle}>
          <Skeleton.Button active block style={{ height: 28 }} />
        </div>
        <div className={styles.goodsPrice}>
          <Skeleton.Button active style={{ height: 26 }} />
        </div>
        <div className={styles.goodsInfo}>
          <div className={styles.goodsIntoContent}>
            <div className={styles.goodsIntoContentItem}>
              <Skeleton.Button active block style={{ height: 20 }} />
            </div>
            <div className={styles.goodsIntoContentItem}>
              <Skeleton.Button active block style={{ height: 20 }} />
            </div>
            <div className={styles.goodsIntoContentItem}>
              <Skeleton.Button active style={{ height: 20 }} />
            </div>
          </div>
        </div>
      </>
    )
  }

  const isFree = state.detail?.planData?.price === 0

  return (
    <>
      <div className={styles.goodsTitle}>
        <p className={styles.goodsTitleText}>{state.detail?.name}</p>
        <IconImage className={styles.vipFree} src='Detail_IconVipFree' />
      </div>
      <div className={styles.goodsPrice}>
        <span className={styles.goodsPriceText}>
          {isFree ? '免费' : `¥${commonUtils.parseCurrency(state.detail?.planData?.price || 0)}`}
        </span>
        <span className={styles.goodsShare}>
          {/* <IconImage width={11} height={13} src='Detail_IconShare'></IconImage> 分享 */}
        </span>
      </div>
      <div className={styles.goodsInfo}>
        {/* <div className={styles.goodsIntoTags}>
    <div className={styles.goodsIntoTag}>封面</div>
    <div className={styles.goodsIntoTag}>平滑</div>
    <div className={styles.goodsIntoTag}>企业</div>
  </div> */}
        <div className={styles.goodsIntoContent}>
          {/* <div className={styles.goodsIntoContentItem}>
            <span>ID：</span>
            <span className={styles.goodsIntoContentItemText}>{state.detail?.number ?? ''}</span>
          </div> */}
          <div className={styles.goodsIntoContentItem}>
            <span>格式：</span>
            <span className={styles.goodsIntoContentItemText}>{state.detail?.fileType}</span>
          </div>
          <div className={styles.goodsIntoContentItem}>
            <span>大小：</span>
            <span className={styles.goodsIntoContentItemText}>
              {commonUtils.formatFileSize(state.detail?.size ?? 0)}
            </span>
          </div>
        </div>
      </div>
    </>
  )
})

// 模版信息
export const DetailBox = observer(({ state }: GoodsDetailProps) => {
  return (
    <>
      <GoodsInfo state={state} />
      <div className={styles.buyTips}>
        购买此作品的用户仅享有使用权，可用于课堂演示、工作汇报、日常使用等场景，不可商用。
      </div>
      <div className={styles.copyrightTips}>若您发现您的合法权利被侵害，请联系我们</div>
      <GroupBtn state={state} />
    </>
  )
})

const VideoCover = observer(({ state }: GoodsDetailProps) => {
  if (state.loading) {
    return <Skeleton.Button active block style={{ width: 650, height: 365 }} />
  }
  return (
    <VideoBox
      key={state.detail?.id}
      src={state.detail?.videoPath}
      poster={commonUtils.getimageView(state.detail?.abbreviatePath ?? '', 650, 365)}
    />
  )
})

// 模版详情
const GoodsDetail = observer(({ state }: GoodsDetailProps) => {
  return (
    <div className={styles.goodsDetail}>
      <div className={styles.goodsDetailContent}>
        <div className={styles.goodsDetailVideo}>
          <VideoCover state={state} />
        </div>
        <div className={styles.goodsDetailInfo}>
          <DetailBox state={state} />
        </div>
      </div>
      <div className={styles.hotRecommend}>
        <div className={styles.hotRecommendTitle}>
          <IconImage src='Detail_IconStar' width={23} />
          热门素材推荐
        </div>
        <RecommendList state={state} />
      </div>
    </div>
  )
})

export default GoodsDetail
